import { useEffect, useRef, useState } from "react";
import type { Editor } from "./canvas";
import type { TLShape } from "@tldraw/tlschema";
import { isSource, sourceName } from "./source-scale";
import { Modal } from "./Modal";
export function CompareDialog({
  editor,
  shapes,
  close,
}: {
  editor: Editor;
  shapes: TLShape[];
  close: () => void;
}) {
  const [urls, setUrls] = useState<string[]>([]),
    [error, setError] = useState(""),
    [opacity, setOpacity] = useState(0.5),
    [x, setX] = useState(0),
    [y, setY] = useState(0),
    [scale, setScale] = useState(1),
    [rotation, setRotation] = useState(0),
    [mode, setMode] = useState("overlay");
  const drag = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const aligned = x !== 0 || y !== 0 || scale !== 1 || rotation !== 0;
  useEffect(() => {
    let live = true;
    const owned: string[] = [];
    void (async () => {
      try {
        for (const s of shapes) {
          const b = editor.getShapePageBounds(s)!;
          const image = await editor.toImage([s.id], {
            format: "png",
            bounds: b,
            padding: 0,
            background: true,
            scale: Math.min(1, 1024 / Math.max(b.w, b.h)),
            pixelRatio: 1,
          });
          if (!live) return;
          owned.push(URL.createObjectURL(image.blob));
        }
        if (live) setUrls([...owned]);
      } catch (e) {
        if (live) setError((e as Error).message);
      }
    })();
    return () => {
      live = false;
      owned.forEach(URL.revokeObjectURL);
    };
  }, [editor, shapes]);
  return (
    <Modal title="Compare alternatives" close={close} wide>
      <p>Manual alignment · originals and calibration stay unchanged.</p>
      {error ? (
        <p role="alert">{error}</p>
      ) : urls.length < 2 ? (
        <p>Preparing two previews…</p>
      ) : (
        <>
          <div className="compare-names">
            {shapes.map((s, i) => (
              <span key={s.id}>
                <b>{i ? "B" : "A"}</b>{" "}
                {isSource(s) ? sourceName(s) : "Selection"}
              </span>
            ))}
          </div>
          <div className="comparison-layout">
            <div
              className="compare-canvas"
              onPointerDown={(event) => {
                if (mode === "a" || event.button !== 0) return;
                drag.current = { x: event.clientX, y: event.clientY, offsetX: x, offsetY: y };
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={(event) => {
                if (!drag.current) return;
                setX(Math.round(drag.current.offsetX + event.clientX - drag.current.x));
                setY(Math.round(drag.current.offsetY + event.clientY - drag.current.y));
              }}
              onPointerUp={(event) => {
                drag.current = null;
                if (event.currentTarget.hasPointerCapture(event.pointerId))
                  event.currentTarget.releasePointerCapture(event.pointerId);
              }}
              onPointerCancel={() => { drag.current = null; }}
            >
              {mode !== "b" && <img src={urls[0]} alt="Alternative A" draggable={false} />}
              {mode !== "a" && (
                <img
                  src={urls[1]}
                  alt="Alternative B"
                  draggable={false}
                  style={{
                    opacity: mode === "overlay" ? opacity : 1,
                    transform: `translate(${x}px,${y}px) rotate(${rotation}deg) scale(${scale})`,
                  }}
                />
              )}
            </div>
            <div className="compare-controls">
              <div
                className="compare-modes"
                role="group"
                aria-label="Comparison view"
              >
                {[
                  ["overlay", "Overlay"],
                  ["a", "A only"],
                  ["b", "B only"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    aria-pressed={mode === value}
                    className={mode === value ? "active" : ""}
                    onClick={() => setMode(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {mode === "overlay" && (
                <label>
                  Overlay opacity · {Math.round(opacity * 100)}%
                  <input
                    aria-label="B opacity"
                    type="range"
                    min="0"
                    max="1"
                    step=".05"
                    value={opacity}
                    onChange={(e) => setOpacity(Number(e.target.value))}
                  />
                </label>
              )}
              <small role="status">{aligned ? "Manually adjusted · drag B to refine" : "Unaligned · drag B to align"}</small>
              <details className="property-disclosure">
                <summary>Align alternatives</summary>
                <div className="form-grid">
                  <label>
                    Horizontal offset
                    <input
                      aria-label="B horizontal"
                      type="number"
                      min="-200"
                      max="200"
                      value={x}
                      onChange={(e) => setX(Number(e.target.value))}
                    />
                  </label>
                  <label>
                    Vertical offset
                    <input
                      aria-label="B vertical"
                      type="number"
                      min="-200"
                      max="200"
                      value={y}
                      onChange={(e) => setY(Number(e.target.value))}
                    />
                  </label>
                  <label>
                    Size · %
                    <input
                      aria-label="B size percent"
                      type="number"
                      min="25"
                      max="200"
                      value={Math.round(scale * 100)}
                      onChange={(e) =>
                        setScale(
                          Math.max(
                            0.25,
                            Math.min(2, Number(e.target.value) / 100),
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    Rotation · °
                    <input
                      aria-label="B rotation"
                      type="number"
                      min="-180"
                      max="180"
                      step="0.5"
                      value={rotation}
                      onChange={(e) => setRotation(Math.max(-180, Math.min(180, Number(e.target.value) || 0)))}
                    />
                  </label>
                </div>
                <button
                  onClick={() => {
                    setX(0);
                    setY(0);
                    setScale(1);
                    setRotation(0);
                    setOpacity(0.5);
                  }}
                >
                  Reset alignment
                </button>
              </details>
            </div>
          </div>
          <small>
            Unaligned until you align it. This view does not detect changes
            automatically.
          </small>
        </>
      )}
    </Modal>
  );
}
